import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Users, CreditCard, Link2, Search, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  location: string;
  preferred_date: string;
  preferred_time: string;
  status: string;
  payment_status: string | null;
  referral_code: string | null;
  message: string | null;
  created_at: string;
}

interface Payment {
  id: string;
  amount: number;
  currency: string | null;
  service_type: string;
  status: string | null;
  stripe_session_id: string | null;
  appointment_id: string | null;
  created_at: string;
}

interface Referral {
  id: string;
  referral_code: string;
  customer_email: string;
  service_type: string;
  service_amount: number;
  commission_amount: number;
  commission_rate: number;
  status: string;
  confirmed_at: string | null;
  paid_at: string | null;
  created_at: string;
}

const Admin = () => {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate("/");
      toast({
        title: "Access Denied",
        description: "You need admin access to view this page.",
        variant: "destructive",
      });
    }
  }, [user, isAdmin, loading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [appointmentsRes, paymentsRes, referralsRes] = await Promise.all([
        supabase.from("appointments").select("*").order("created_at", { ascending: false }),
        supabase.from("payments").select("*").order("created_at", { ascending: false }),
        supabase.from("referrals").select("*").order("created_at", { ascending: false }),
      ]);

      if (appointmentsRes.data) setAppointments(appointmentsRes.data);
      if (paymentsRes.data) setPayments(paymentsRes.data);
      if (referralsRes.data) setReferrals(referralsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast({ title: "Error", description: "Failed to load data.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAppointments = appointments.filter(
    (a) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.phone.includes(searchTerm) ||
      a.service.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusColor = (status: string) => {
    switch (status) {
      case "confirmed": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "cancelled": return "bg-red-100 text-red-800";
      case "completed": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const paymentStatusColor = (status: string | null) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "failed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  if (!isAdmin) return null;

  const totalRevenue = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);

  const uniqueClients = new Set(appointments.map((a) => a.email)).size;

  return (
    <>
      <SEO title="Admin Dashboard | NearLab Services" description="Admin dashboard for managing appointments, payments, and referrals." />

      <div className="min-h-screen bg-muted/30 py-8 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">Dashboard</h1>
              <p className="text-muted-foreground">Manage appointments, clients & payments</p>
            </div>
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-blue-primary" />
                  <div>
                    <p className="text-2xl font-bold">{appointments.length}</p>
                    <p className="text-sm text-muted-foreground">Appointments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-green-primary" />
                  <div>
                    <p className="text-2xl font-bold">{uniqueClients}</p>
                    <p className="text-sm text-muted-foreground">Clients</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-8 w-8 text-blue-primary" />
                  <div>
                    <p className="text-2xl font-bold">${(totalRevenue / 100).toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Link2 className="h-8 w-8 text-green-primary" />
                  <div>
                    <p className="text-2xl font-bold">{referrals.length}</p>
                    <p className="text-sm text-muted-foreground">Referrals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="appointments">
            <TabsList className="mb-4">
              <TabsTrigger value="appointments">Appointments ({appointments.length})</TabsTrigger>
              <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
              <TabsTrigger value="referrals">Referrals ({referrals.length})</TabsTrigger>
            </TabsList>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              <div className="space-y-3">
                {filteredAppointments.length === 0 ? (
                  <Card><CardContent className="p-6 text-center text-muted-foreground">No appointments found.</CardContent></Card>
                ) : (
                  filteredAppointments.map((apt) => (
                    <Card key={apt.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{apt.name}</h3>
                              <Badge className={statusColor(apt.status)}>{apt.status}</Badge>
                              {apt.payment_status && (
                                <Badge className={paymentStatusColor(apt.payment_status)}>
                                  {apt.payment_status}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{apt.service}</p>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
                              <span>{apt.email}</span>
                              <span>{apt.phone}</span>
                              <span>{apt.location}</span>
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <p className="font-medium">
                              {new Date(apt.preferred_date).toLocaleDateString()} at {apt.preferred_time}
                            </p>
                            <p className="text-muted-foreground">
                              Booked {new Date(apt.created_at).toLocaleDateString()}
                            </p>
                            {apt.referral_code && (
                              <Badge variant="outline" className="mt-1">Ref: {apt.referral_code}</Badge>
                            )}
                          </div>
                        </div>
                        {apt.message && (
                          <p className="text-sm text-muted-foreground mt-2 pt-2 border-t italic">"{apt.message}"</p>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments">
              <div className="space-y-3">
                {payments.length === 0 ? (
                  <Card><CardContent className="p-6 text-center text-muted-foreground">No payments found.</CardContent></Card>
                ) : (
                  payments.map((pmt) => (
                    <Card key={pmt.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold">{pmt.service_type}</h3>
                            <p className="text-sm text-muted-foreground">
                              {new Date(pmt.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">${(pmt.amount / 100).toFixed(2)}</p>
                            <Badge className={paymentStatusColor(pmt.status)}>
                              {pmt.status || "unknown"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Referrals Tab */}
            <TabsContent value="referrals">
              <div className="space-y-3">
                {referrals.length === 0 ? (
                  <Card><CardContent className="p-6 text-center text-muted-foreground">No referrals found.</CardContent></Card>
                ) : (
                  referrals.map((ref) => (
                    <Card key={ref.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{ref.customer_email}</h3>
                              <Badge className={statusColor(ref.status)}>{ref.status}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {ref.service_type} · Code: {ref.referral_code}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">${ref.commission_amount.toFixed(2)}</p>
                            <p className="text-sm text-muted-foreground">{ref.commission_rate}% of ${ref.service_amount.toFixed(2)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default Admin;
